/**
 * WGS84 <-> viewer-local coordinate conversions for the OSM minimap.
 *
 * Lives in its own Leaflet-free module so the conversions are testable in
 * plain Node (Leaflet touches `window` at import time). These conversions
 * are orientation-aid maths only — they never feed any LOS result.
 *
 * Accuracy: delegates the actual proj4 EPSG:27700 <-> WGS84 conversion to
 * the shared visual-only module (core/visualProjection.ts, issue #40); see
 * its ACCURACY_NOTE for the Helmert-vs-OSTN15 caveat — fine for a camera
 * jump, never for analysis.
 */
import { bngToLocal, localToBng } from '../core/coordinates';
import { bngToWgs84, wgs84ToBng } from '../core/visualProjection';

/** Viewer-local (x, z) metres → WGS84 lon/lat degrees. */
export function localToLonLat(x: number, z: number): { lon: number; lat: number } {
  const { easting, northing } = localToBng(x, z);
  return bngToWgs84(easting, northing);
}

/** WGS84 lon/lat degrees → viewer-local (x, z) metres. Inverse of localToLonLat. */
export function lonLatToLocal(lon: number, lat: number): { x: number; z: number } {
  const { easting, northing } = wgs84ToBng(lon, lat);
  return bngToLocal(easting, northing);
}
